// controllers
import AnimationController from '../../assets/js/controllers/animation';
import LogController from '../../assets/js/controllers/log';
import ResizeController from '../../assets/js/controllers/resize';

// helpers
import { validateParamBoolean } from '../../assets/js/helpers';

// constants
import Platform from '../../assets/js/constants/platform';
import VideoEvent from '../../assets/js/constants/video-event';

export default class Video {

	static selector = '[data-video]';

	// class variables

	config = { };

	el = {};

	controllers = {
		resize: null
	};

	flags = {
		isDisabled: false,
		isLoaded: false,
		isLoadPending: false,
		isPlaying: false,
		isMuted: false
	};

	promises = {
		play: null
	};

	states = {
		currentTime: 0,
		duration: 0,
		ratio: 0,
		src: null
	};

	// constructor

	constructor(container, video = null) {
		this.el = {
			container,
			video: video || container.querySelector('video')
		};

		this.config = { ...this.defaults };

		// intialize
		this.initialize();
	}

	// init

	initialize() {
		if (this.el.video) {
			const willMute = validateParamBoolean(this.el.video, 'muted', false, true);
			const willPlay = validateParamBoolean(this.el.container, 'data-video-autoplay', false, true);

			if (willPlay) {
				this.play();
			}

			if (willMute) {
				this.mute();
			}

			this.#addEventListeners();

		} else {
			this.el.container.classList.add('is-video-missing');
		}
	}

	// event listeners

	#addEventListeners = () => {
		// external events
		this.el.container.addEventListener(VideoEvent.PAUSE, this.#onPause);
		this.el.container.addEventListener(VideoEvent.PLAY, this.#onPlay);
		this.el.container.addEventListener(VideoEvent.REPLAY, this.#onReplay);
		this.el.container.addEventListener(VideoEvent.SEEK, this.#onSeek);
		this.el.container.addEventListener(VideoEvent.MUTE, this.#onMute);
		this.el.container.addEventListener(VideoEvent.UNMUTE, this.#onUnmute);

		this.el.container.addEventListener(VideoEvent.LOAD, this.#onLoad);
		this.el.container.addEventListener(VideoEvent.RELOAD, this.#onReload);
		this.el.container.addEventListener(VideoEvent.UNLOAD, this.#onUnload);
		this.el.container.addEventListener(VideoEvent.STATUS_QUERY, this.#onStatusQuery);

		// video events
		this.el.video.addEventListener('loadedmetadata', this.#onVideoEventLoadedMetaData);
		this.el.video.addEventListener('pause', this.#onVideoEventPause);
		this.el.video.addEventListener('play', this.#onVideoEventPlay); // fires immediately when toggled
		this.el.video.addEventListener('playing', this.#onVideoEventPlaying); // only fires once buffer allows video to play

		// controller listeners
		this.controllers.resize = ResizeController.set({
			update: this.#onResizeUpdate,
			render: this.#onResizeRender
		});
	}

	#removeEventListeners = () => {
		// external events
		this.el.container.removeEventListener(VideoEvent.PAUSE, this.#onPause);
		this.el.container.removeEventListener(VideoEvent.PLAY, this.#onPlay);
		this.el.container.removeEventListener(VideoEvent.REPLAY, this.#onReplay);
		this.el.container.removeEventListener(VideoEvent.SEEK, this.#onSeek);
		this.el.container.removeEventListener(VideoEvent.MUTE, this.#onMute);
		this.el.container.removeEventListener(VideoEvent.UNMUTE, this.#onUnmute);

		this.el.container.removeEventListener(VideoEvent.LOAD, this.#onLoad);
		this.el.container.removeEventListener(VideoEvent.RELOAD, this.#onReload);
		this.el.container.removeEventListener(VideoEvent.UNLOAD, this.#onUnload);
		this.el.container.removeEventListener(VideoEvent.STATUS_QUERY, this.#onStatusQuery);

		// video events
		this.el.video.removeEventListener('loadedmetadata', this.#onVideoEventLoadedMetaData);
		this.el.video.removeEventListener('pause', this.#onVideoEventPause);
		this.el.video.removeEventListener('play', this.#onVideoEventPlay); // fires immediately when toggled
		this.el.video.removeEventListener('playing', this.#onVideoEventPlaying); // only fires once buffer allows video to play

		// controller listeners
		ResizeController.clear(this.controllers.resize);
	}

	// video commands

	#onPause = () => {
		this.pause();
	}

	#onPlay = () => {
		this.play();
	}

	#onReplay = () => {
		this.replay();
	}

	#onSeek = (e) => {
		if (e.detail.time) {
			this.seek(e.detail.time);
		}
	}

	#onMute = () => {
		this.mute();
	}

	#onUnmute = () => {
		this.unmute();
	}

	#onLoad = (e) => {
		if (e.detail.src) {
			// TODO: change source
		} else {
			this.load();
		}
	}

	#onReload = (e) => {
		this.reload(e.detail.willPlay === true);
	}

	#onUnload = () => {
		this.unload();
	}

	#onStatusQuery = () => {
		this.el.container.dispatchEvent(new CustomEvent(VideoEvent.STATUS, { detail: { ...this.states } }));
	}

	// video status events

	#onVideoEventLoadedMetaData = () => {
		this.states.duration = this.el.video.duration;
		this.el.container.dispatchEvent(new CustomEvent(VideoEvent.LOADING_META_DATA, { detail: { ...this.states } }));
	}

	#onVideoEventPause = () => {
		this.pauseRender();
	}

	#onVideoEventPlay = () => {
		this.playRender();
	}

	#onVideoEventPlaying = () => {
		this.playingStatusRender();
	}

	// video play promise

	#onVideoPromiseComplete = () => {
		this.promises.play = null;

		if (this.flags.isLoadPending) {
			this.flags.isLoadPending = false;
			this.load(this.flags.isPlaying);
		} else if (!this.flags.isPlaying) {
			this.pause();
		}
	}

	#onVideoPromiseFail = () => {
		this.promises.play = null;
	}

	// controller methods

	#onAnimationUpdate = () => {
		this.states.currentTime = this.el.video.currentTime;
		this.states.ratio = (this.states.currentTime / this.states.duration) || 0;
	}

	#onAnimationRender = () => {}

	#onResizeUpdate = () => ResizeController.isPlatformChange;

	#onResizeRender = () => {
		if (this.flags.isLoaded || this.flags.isPlaying) {
			this.reload(this.flags.isPlaying);
		}
	}

	//

	togglePlay() {
		if (this.flags.isPlaying) {
			this.pause();
		} else {
			this.play();
		}
	}

	pause() {
		this.pauseRender();

		if (!this.promises.play) {
			this.el.video.pause();
		}

		if (this.controllers.animation) {
			AnimationController.clear(this.controllers.animation);
			this.controllers.animation = null;
		}
	}

	pauseRender() {
		this.flags.isPlaying = false;
		this.el.container.classList.remove('is-video-playing');
		this.el.container.dispatchEvent(new CustomEvent(VideoEvent.PAUSING, { detail: { ...this.states } }));
	}

	play() {
		if (!this.flags.isLoaded) {
			this.load(true);
		} else if (this.states.ratio === 1) {
			this.replay();
		} else if (!this.flags.isPlaying && !this.promises.play) {
			this.playRender();

			try {
				this.promises.play = this.el.video.play();
				if (this.promises.play) {
					this.promises.play.then(this.#onVideoPromiseComplete, this.#onVideoPromiseFail);
				}

				if (!this.controllers.animation) {
					this.controllers.animation = AnimationController.set({
						update: this.#onAnimationUpdate,
						render: this.#onAnimationRender
					});
				}
			} catch (error) {
				LogController.warn(error);
			}
		}
	}

	playRender() {
		this.flags.isPlaying = true;
		this.el.container.classList.add('is-video-playing');
		this.el.container.dispatchEvent(new CustomEvent(VideoEvent.PLAYING, { detail: { ...this.states } }));
	}

	replay(play = true) {
		this.seek(0);
		if (play) {
			this.play();
		}
	}

	// video is playing (buffer complete)

	playingStatusRender() {
		this.el.container.dispatchEvent(new CustomEvent(VideoEvent.PLAYING_STATUS, { detail: { ...this.states } }));
	}

	// begin loading video (based on breakpoint)

	load(willPlay = true) {

		if (!this.flags.isLoaded && !this.promises.play) {

			// select source from breakpoint

			let $source = null;
			if (ResizeController.platform === Platform.MOBILE) {
				$source = this.el.video.querySelector('source[data-src-mobile]');
			} else if (ResizeController.platform === Platform.TABLET) {
				$source = this.el.video.querySelector('source[data-src-tablet]');
			} else {
				$source = this.el.video.querySelector('source[data-src-desktop]');
			}

			// validate that source exists

			let isSourceChange = false;
			const src = $source ? $source.getAttribute('data-src') : null;

			if (src) {
				if (src !== this.states.src) {
					isSourceChange = true;
				}
				$source.setAttribute('src', src);

			} else {
				this.el.container.dispatchEvent(new CustomEvent(VideoEvent.LOADING_ERROR, { detail: { ...this.states } }));
			}

			// begin loading if source is valid

			if (isSourceChange) {
				this.el.container.classList.add('is-video-active');
				this.el.container.dispatchEvent(new CustomEvent(VideoEvent.LOADING_SUCCESS, { detail: { ...this.states } }));

				this.states.src = src;

				try {
					this.flags.isLoaded = true;
					this.el.video.load();

					if (willPlay && this.el.video.paused) {
						this.play();
					} else if (!willPlay) {
						this.pause();
					}
				} catch (error) {
					this.flags.isLoaded = false;
					this.el.container.dispatchEvent(new CustomEvent(VideoEvent.LOADING_ERROR, { detail: { ...this.states } }));
				}
			}

		} else if (this.flags.isLoaded && willPlay) {
			this.play();
		}
	}

	// remove all src attributes from children (maintaining data-src), prevent further loading of video

	unload() {
		if (this.flags.isLoaded) {
			this.flags.isLoaded = false;
			this.flags.isPlaying = false;
			this.states.src = null;
			this.promises.play = null;

			const $sources = this.el.video.children;
			for (let i = 0; i < $sources.length; i++) {
				const $source = $sources[i];
				$source.removeAttribute('src');
			}
			this.el.container.classList.remove('is-video-active');
			this.el.container.dispatchEvent(new CustomEvent(VideoEvent.UNLOADING, { detail: { ...this.states } }));
		}
	}

	// kill any connections / loading, start over

	reload(willPlay = true) {
		this.el.container.dispatchEvent(new CustomEvent(VideoEvent.RELOADING, { detail: { ...this.states } }));
		this.unload();
		this.load(willPlay);
	}

	// seek to x seconds

	seek(time = 0) {
		const timeFloat = parseFloat(time);
		if (!isNaN(timeFloat)) {
			this.el.video.currentTime = timeFloat;
			this.states.progress = timeFloat;
			this.states.ratio = this.states.progress / this.states.duration;
			this.el.container.dispatchEvent(new CustomEvent(VideoEvent.SEEKING, { detail: { ...this.states } }));
		}
	}

	// video sound

	toggleAudio() {
		if (!this.flags.isMuted) {
			this.mute();
		} else {
			this.unmute();
		}
	}

	mute() {
		this.flags.isMuted = true;
		this.el.video.volume = 0;
		this.el.container.classList.add('is-muted');
	}

	unmute() {
		this.flags.isMuted = false;
		this.el.video.volume = 1;
		this.el.container.classList.remove('is-muted');
	}

	// destroy

	destroy() {
		this.unload();
		this.#removeEventListeners();
	}

	// getters

	get currentTime() {
		return this.states.currentTime;
	}

	get duration() {
		return this.states.duration;
	}

	get el() {
		return this.el.container;
	}

	get ratio() {
		return this.states.ratio;
	}

	get video() {
		return this.el.video;
	}

	// states

	get isPlaying() {
		return this.flags.isPlaying;
	}

}
